import React from 'react';
import PropTypes from 'prop-types';
import uuid4 from 'uuid4';

import { uiConfig } from '../../../../../conf/listItems';

// Components
import FeedbackCard from '../../../../../../components/FeedbackCard';
import Form from '../../../../../../components/Form';
import { DialogBody } from '../../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../../components/DialogFooter';
import { Button } from '../../../../../../components/Button';

// Utils
import { translateUIConfigForAddEdit } from '../../../../../../common/utility';

export const ListItemEditableForm = ({
    isNew,
    data,
    closeDialog,
    saveDisabled,
    onApply,
    listId,
    type,
    isExistingDoc
}) => {
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);

    const documentState = React.useMemo(() => {
        if (!isNew && data) {
            return data;
        } else {
            return typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {};
        }
    }, [data]);

    const formModel = translateUIConfigForAddEdit(uiConfig);
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            const val = field.accessorOverride?.(formState);

            let { valid, failed } = field.validation(val);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareDocument = ({ formState, data, isNew }) => {
        const preparedDocument = ((doc, originalState) => {
            const { value, label, ...otherDocValues } = doc;

            return {
                ...originalState,
                adHocId: value ?? originalState.adHocId,
                adHocLabel: label ?? originalState.adHocLabel,
                ...otherDocValues,
                itemType: data.itemType ?? type,
                listId: data.listId ?? listId
            };
        })(formState, data);

        return isNew
            ? {
                  ...preparedDocument,
                  dtoState: 'NEW',
                  uuid: uuid4()
              }
            : {
                  ...preparedDocument,
                  dtoState: data.dtoState === 'NEW' ? 'NEW' : 'UPDATED'
              };
    };
    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        updateCompose(false);
        let update = prepareDocument({ formState, data: documentState, isNew });
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('Matching document already exists in this list.');
            return;
        }
        onApply(update);
        closeDialog();
    };

    return (
        <>
            <DialogBody>
                {fatalError != null && (
                    <FeedbackCard
                        intent={'danger'}
                        feedback={{
                            heading: `An error has occurred.`,
                            message:
                                typeof fatalError === 'string'
                                    ? fatalError
                                    : `Please wait a few moments before trying your request
again.`
                        }}
                        details={fatalError}
                    />
                )}
                <Form
                    formModel={formModel}
                    data={documentState}
                    errors={errors}
                    compose={compose}
                    onApplyFormState={onApplyFormState}
                    setEdited={setEdited}
                    fatalError={fatalError}
                />
            </DialogBody>
            <DialogFooter>
                <Button
                    text
                    appearance="primary"
                    onClick={() => updateCompose(true)}
                    disabled={!edited || saveDisabled}
                >
                    Apply
                </Button>
                <Button text appearance="secondary" onClick={closeDialog}>
                    Cancel
                </Button>
            </DialogFooter>
        </>
    );
};

export default ListItemEditableForm;

ListItemEditableForm.propTypes = {
    data: PropTypes.object,
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onApply: PropTypes.func,
    saveDisabled: PropTypes.bool,
    listId: PropTypes.string,
    type: PropTypes.string
};
